import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useIdentity } from "@/auth/identity";
import { toast } from "@/hooks/use-toast";
import { Truck, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { tripService } from "@/services/api";
import { useDriver } from "@/context/DriverContext";
import BottomNavigation from "@/components/BottomNavigation";

const StartTrip = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useIdentity();
  const { todayShipments, updateShipmentStatus } = useDriver();

  // El id viene de la tarjeta que lanzó la navegación. Si se perdió (refresh)
  // y hay una sola ruta hoy, se usa esa; con dos, no hay forma de adivinar.
  const stateId = (location.state as { shipmentId?: string } | null)?.shipmentId;
  const shipmentId =
    stateId ?? (todayShipments.length === 1 ? todayShipments[0].id : null);

  useEffect(() => {
    if (!shipmentId) {
      toast({
        title: "Elegí la ruta",
        description: "Volvé al inicio y tocá la ruta que querés iniciar.",
        variant: "destructive",
      });
      navigate("/");
    }
  }, [shipmentId, navigate]);

  const [formData, setFormData] = useState({
    routeNumber: "",
    packagesCount: 0,
    vehiclePlate: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingShipment, setIsLoadingShipment] = useState(true);
  const [shipmentData, setShipmentData] = useState<any>(null);

  // Cargar información completa del viaje (el contexto no guarda el vehículo)
  useEffect(() => {
    const loadShipment = async () => {
      if (!shipmentId) {
        setIsLoadingShipment(false);
        return;
      }

      try {
        const response = await tripService.getTodayShipment();
        const shipments = response.data?.data;

        // El back devuelve el id numérico; shipmentId es string.
        const shipment = Array.isArray(shipments)
          ? shipments.find(
              (s: { id: number | string }) => String(s.id) === shipmentId
            )
          : undefined;

        if (shipment) {
          setShipmentData(shipment);

          // Pre-llenar solo la patente del vehículo para mostrar
          setFormData(prev => ({
            ...prev,
            // Si hay información de vehículo asignado, usarla
            vehiclePlate: shipment.vehicle?.plate || shipment.vehiclePlate || "",
          }));
        }
      } catch (error) {
        console.error("Error cargando información del viaje:", error);
        toast({
          title: "Error",
          description: "No se pudo cargar la información del viaje",
          variant: "destructive",
        });
      } finally {
        setIsLoadingShipment(false);
      }
    };

    loadShipment();
  }, [shipmentId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validar que todos los campos estén completos
    if (
      !formData.routeNumber ||
      !formData.packagesCount
    ) {
      toast({
        title: "Error",
        description: "Por favor complete todos los campos",
        variant: "destructive",
      });
      return;
    }

    // Validar número de ruta (entre 6 y 11 caracteres)
    const routeLen = formData.routeNumber.length;
    if (routeLen < 6 || routeLen > 11) {
      toast({
        title: "Error",
        description: "El número de ruta debe tener entre 6 y 11 caracteres",
        variant: "destructive",
      });
      return;
    }

    // Validar cantidad de paquetes (número positivo)
    if (formData.packagesCount <= 0) {
      toast({
        title: "Error",
        description: "La cantidad de paquetes debe ser un número positivo",
        variant: "destructive",
      });
      return;
    }

    // Verificar que hay vehículo asignado
    if (!shipmentData?.vehicle?.plate && !formData.vehiclePlate) {
      toast({
        title: "Error",
        description: "No hay vehículo asignado para este viaje",
        variant: "destructive",
      });
      return;
    }

    if (!shipmentId) {
      toast({
        title: "Error",
        description: "No hay un viaje asignado para hoy",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const shipmentUpdateData = {
        attended: true,
        status: "EN_CURSO",
        routeId: formData.routeNumber,
        vehiclePlate: shipmentData?.vehicle?.plate || formData.vehiclePlate,
        packages: formData.packagesCount,
      };

      await tripService.startShipment(shipmentId, shipmentUpdateData);

      // Actualizar el estado de ESA ruta en el contexto
      updateShipmentStatus(shipmentId, "EN_CURSO");

      toast({
        title: "Éxito",
        description: "Viaje iniciado correctamente",
      });

      navigate("/");
    } catch (error: any) {
      console.error(error);

      // Verificar si es un error 404 (patente no encontrada)
      if (error?.response?.status === 404) {
        toast({
          title: "Error",
          description: "La patente del vehículo no se encuentra en el sistema",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: "No se pudo iniciar el viaje",
          variant: "destructive",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;

    // Convertir patente del vehículo a mayúsculas automáticamente
    const processedValue = name === "vehiclePlate" ? value.toUpperCase() : value;

    setFormData({
      ...formData,
      [name]: type === "number" ? Number(processedValue) : processedValue,
    });
  };

  return (
    <div className="min-h-screen bg-gray-100 pb-16">
      {/* Header */}
      <header className="bg-primary text-white p-4 flex items-center">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          className="text-white mr-2"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-xl font-bold">Iniciar Viaje</h1>
        </div>
      </header>

      {/* Form */}
      <main className="container mx-auto p-4">
        {isLoadingShipment ? (
          <div className="bg-white p-8 rounded-lg shadow text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-gray-600">Cargando información del viaje...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="routeNumber" className="block mb-2">
                    Número de ruta *
                  </Label>
                  <Input
                    id="routeNumber"
                    name="routeNumber"
                    placeholder="Ej: 546712 (6 a 11 caracteres)"
                    value={formData.routeNumber}
                    onChange={handleInputChange}
                    className="w-full"
                    maxLength={11}
                  />
                </div>

                <div>
                  <Label htmlFor="packagesCount" className="block mb-2">
                    Cantidad de paquetes *
                  </Label>
                  <Input
                    id="packagesCount"
                    name="packagesCount"
                    type="number"
                    min="1"
                    placeholder="Ej: 45"
                    value={formData.packagesCount || ""}
                    onChange={handleInputChange}
                    className="w-full"
                  />
                </div>

                <div>
                  <Label className="block mb-2">
                    Vehículo asignado
                  </Label>
                  <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                    {shipmentData?.vehicle ? (
                      <div className="text-sm">
                        <div className="font-medium text-gray-900">
                          {shipmentData.vehicle.plate}
                        </div>
                        <div className="text-gray-600">
                          {shipmentData.vehicle.brand} {shipmentData.vehicle.model}
                          {shipmentData.vehicle.unitType && ` - ${shipmentData.vehicle.unitType.name}`}
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">
                        No hay vehículo asignado
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <Button type="submit" className="w-full py-6" disabled={isSubmitting}>
              {isSubmitting ? "Procesando..." : "Iniciar Viaje"}
            </Button>
          </form>
        )}
      </main>

      <BottomNavigation />
    </div>
  );
};

export default StartTrip;
